import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, Tabs, TabsList, TabsTrigger } from '@onward-delivery/design';

import { TABS_LIST_CLASS, TABS_TRIGGER_CLASS } from '../../constants';
import BadgeTag from './BadgeTag';
import { badgeGuideEntries, guideHeading, tabGuideEntries } from '../guide';

const GUIDE_SECTIONS = [
    { value: 'tabs', label: 'Tabs' },
    { value: 'badges', label: 'Badges' },
];

const Points = ({ points }) => (
    <ul className="mt-1 flex list-disc flex-col gap-0.5 pl-5 text-[13px] text-slate-600">
        {points.map((point) => (
            <li key={point}>{point}</li>
        ))}
    </ul>
);

const TabsGuide = ({ kind }) => (
    <>
        <p className="mb-2 text-[13px] text-slate-600">{guideHeading(kind).intro}</p>
        <ul className="flex flex-col gap-2">
            {tabGuideEntries(kind).map((tab) => (
                <li key={tab.key} className={`rounded-md border px-3 py-2.5 ${tab.panelClass}`}>
                    <p className="text-[13px] font-semibold text-slate-900">{tab.label}</p>
                    <Points points={tab.points} />
                </li>
            ))}
        </ul>
    </>
);

const BadgesGuide = ({ kind }) => (
    <ul className="flex flex-col divide-y divide-slate-200">
        {badgeGuideEntries(kind).map((badge) => (
            <li key={badge.key} className="flex items-start gap-3 py-2.5">
                <div className="w-44 shrink-0">
                    <BadgeTag variant={badge.variant}>{badge.sample}</BadgeTag>
                </div>
                <Points points={badge.points} />
            </li>
        ))}
    </ul>
);

/**
 * The onboarding info dialog for `kind`, with a selector between what each tab holds and what each badge means.
 * Reference content only, so the caller owns its open state. It reopens on Tabs.
 *
 * @param {{ kind?: 'carrier'|'shipper', open: boolean, onClose: Function }} props
 */
const GuideDialog = ({ kind = 'carrier', open, onClose }) => {
    const [section, setSection] = useState(GUIDE_SECTIONS[0].value);

    const close = () => {
        setSection(GUIDE_SECTIONS[0].value);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !next && close()}>
            <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto" aria-describedby={undefined}>
                <DialogTitle>{guideHeading(kind).title}</DialogTitle>

                <Tabs value={section} onValueChange={setSection} className="mt-4">
                    <TabsList className={TABS_LIST_CLASS}>
                        {GUIDE_SECTIONS.map((s) => (
                            <TabsTrigger key={s.value} value={s.value} className={TABS_TRIGGER_CLASS}>
                                {s.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>

                <div className="mt-4">
                    {section === 'tabs' ? <TabsGuide kind={kind} /> : <BadgesGuide kind={kind} />}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default GuideDialog;
